import React, { useEffect, useState } from 'react';
import { HiX, HiOutlineExternalLink, HiOutlineClipboard, HiOutlineCheck } from 'react-icons/hi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import StatusBadge from './StatusBadge';
import PlatformIcons from './PlatformIcons';
import CredentialRow from './CredentialRow';

const urlLabel = {
  production: 'Production',
  staging: 'Staging',
  admin: 'Admin Panel',
  demo: 'Demo',
  github: 'Repository',
  docs: 'Docs / Page',
};

const UrlPill = ({ kind, url }) => {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (err) {
      /* noop */
    }
  };

  // Strip protocol for display so more of the meaningful URL fits on mobile.
  // Copy + Open still use the full URL.
  const displayUrl = url.replace(/^https?:\/\//, '');

  return (
    <div className="flex items-center gap-1 rounded-lg bg-primary-50/70 p-1.5 pl-2.5 ring-1 ring-primary-100 sm:gap-2 sm:p-2 sm:pl-3">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-primary-600">
          {urlLabel[kind] || kind}
        </div>
        <div className="truncate font-mono text-[11px] text-neutral-dark sm:text-xs" title={url}>
          {displayUrl}
        </div>
      </div>
      <button
        onClick={copy}
        title="Copy URL"
        aria-label="Copy URL"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary-600 transition-colors hover:bg-primary-100"
      >
        {copied ? (
          <HiOutlineCheck className="h-4 w-4 text-emerald-600" />
        ) : (
          <HiOutlineClipboard className="h-4 w-4" />
        )}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title="Open"
        aria-label="Open URL"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-500 text-white transition-colors hover:bg-primary-600"
      >
        <HiOutlineExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
};

const StoreButton = ({ kind, url }) => {
  const isIos = kind === 'ios';
  const Icon = isIos ? FaApple : FaGooglePlay;
  const label = isIos ? 'Download on the' : 'Get it on';
  const store = isIos ? 'App Store' : 'Google Play';

  // Public, dependency-free QR code via a free QR API
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(url)}`;

  return (
    <div className="flex items-stretch gap-3 rounded-xl bg-neutral-dark p-3 text-white">
      <div className="shrink-0 self-center rounded-md bg-white p-1">
        <img src={qrUrl} alt={`${store} QR`} className="h-16 w-16 sm:h-20 sm:w-20" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 shrink-0 sm:h-6 sm:w-6" />
          <div className="min-w-0">
            <div className="truncate text-[10px] leading-tight opacity-75">{label}</div>
            <div className="truncate text-sm font-semibold leading-tight">{store}</div>
          </div>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center justify-center rounded-md bg-white/15 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/25"
        >
          Open Store Page
        </a>
      </div>
    </div>
  );
};

const ProjectDetailModal = ({ project, onClose }) => {
  // Lock scroll while modal is open + ESC to close
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!project) return null;

  const {
    id,
    name,
    tagline,
    description,
    image,
    category,
    status,
    year,
    client,
    techStack = [],
    platforms = [],
    urls = {},
    appStores = {},
    credentials = [],
  } = project;

  const CREDENTIALS_VISIBLE_FOR = new Set([
    'hexapi-delivery',
    'smart-school',
    'school-management-systems',
  ]);
  const showCredentials = CREDENTIALS_VISIBLE_FOR.has(id);

  const urlEntries = Object.entries(urls).filter(([kind, v]) => Boolean(v) && kind !== 'github');
  const hasStores = Boolean(appStores.ios || appStores.android);

  return (
    <div
      className="fixed inset-0 z-[100] flex animate-fade-in items-end justify-center bg-neutral-dark/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[95vh] w-full animate-slide-up flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-h-[90vh] sm:max-w-3xl sm:rounded-2xl"
      >
        {/* Cover */}
        <div className="relative h-44 shrink-0 overflow-hidden bg-gradient-to-br from-primary-100 to-accent-100 sm:h-56">
          <img src={image} alt={name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-dark/90 via-neutral-dark/30 to-transparent" />

          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-md transition-colors hover:bg-white/25"
          >
            <HiX className="h-5 w-5" />
          </button>

          <div className="absolute bottom-3 left-4 right-4 text-white sm:bottom-4">
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5 sm:mb-2 sm:gap-2">
              <StatusBadge status={status} size="lg" />
              <span className="rounded-md bg-primary-600/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-sm">
                {category}
              </span>
              {year && (
                <span className="rounded-md bg-white/15 px-2 py-1 text-[10px] font-semibold ring-1 ring-white/25 backdrop-blur-md">
                  {year}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold leading-tight sm:text-2xl md:text-3xl">{name}</h2>
            <p className="mt-1 line-clamp-2 text-xs text-white/85 sm:line-clamp-none sm:text-sm">
              {tagline}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:space-y-7 sm:px-7 sm:py-6">
          {/* Meta row */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="text-neutral">Client:</span>{' '}
              <span className="font-medium text-neutral-dark">{client || '—'}</span>
            </div>
            <PlatformIcons platforms={platforms} size="lg" />
          </div>

          {/* Description */}
          {description && (
            <section>
              <h3 className="section-subheading">Overview</h3>
              <p className="text-sm leading-relaxed text-neutral-dark/90">{description}</p>
            </section>
          )}

          {/* Tech stack */}
          {techStack.length > 0 && (
            <section>
              <h3 className="section-subheading">Tech Stack</h3>
              <div className="flex flex-wrap gap-1.5">
                {techStack.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 ring-1 ring-primary-100"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* URLs */}
          {urlEntries.length > 0 && (
            <section>
              <h3 className="section-subheading">Links</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {urlEntries.map(([kind, url]) => (
                  <UrlPill key={kind} kind={kind} url={url} />
                ))}
              </div>
            </section>
          )}

          {/* App stores */}
          {hasStores && (
            <section>
              <h3 className="section-subheading">App Stores</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {appStores.ios && <StoreButton kind="ios" url={appStores.ios} />}
                {appStores.android && <StoreButton kind="android" url={appStores.android} />}
              </div>
            </section>
          )}

          {/* Credentials (only for allow-listed projects) */}
          {showCredentials && credentials.length > 0 && (
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="section-subheading m-0">Credentials</h3>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-600">
                  Internal use only
                </span>
              </div>
              <div className="space-y-2.5">
                {credentials.map((c, i) => (
                  <CredentialRow key={`${c.role}-${i}`} cred={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailModal;
